import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import apiInstance from '../../utils/axios';
import UserData from '../plugin/UserData';
import getCartID from '../plugin/cartID';
import GetCurrentAddress from '../plugin/UserCountry';
import { CartContext } from '../../App';
import { useAuthStore } from '../../store/auth';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from '../../components/ui/Toast';
import { 
  ShoppingCart, Trash2, Plus, Minus, ArrowLeft, ArrowRight, 
  User, Mail, Phone, MapPin, Building, Map, Package, 
  CreditCard, CheckCircle, MessageCircle, Loader, ChevronRight,
  ShoppingBag, Truck
} from 'lucide-react';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '0.00';
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

function Cart() {
  const { t } = useLanguage();
  const [cart, setCart] = useState([]);
  const [cartTotal, setCartTotal] = useState({});
  const [productQuantities, setProductQuantities] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const hasFetched = useRef(false);

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('Paraguay');

  const [cartCount, setCartCount] = useContext(CartContext);
  const userData = UserData();
  const cart_id = getCartID();
  const navigate = useNavigate();
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn);
  const whatsappNumber = '+595971308910'; // Fixed WhatsApp number

  const getVariantImage = (product, color) => {
    if (!product) return '';
    try {
      if (color && color !== 'Sin Color' && product.colors) {
        const colorObj = product.colors.find(c => c.name === color);
        if (colorObj && colorObj.image) return colorObj.image;
      }
      return product.image || '';
    } catch (error) {
      return product.image || '';
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && cart.length === 0) {
      toast.warning(t('emptyCart') || 'Tu carrito está vacío');
      return;
    }
    if (currentStep === 2) {
      if (!fullName || !mobile || !address || !city || !state) {
        toast.warning(t('fillAllFields') || 'Completa todos los campos obligatorios');
        return;
      }
    }
    if (currentStep < 3) setCurrentStep(currentStep + 1);
  };
  const prevStep = () => currentStep > 1 && setCurrentStep(currentStep - 1);

  const fetchCartData = async (cartId, userId) => {
    try {
      const url = userId ? `cart-list/${cartId}/${userId}/` : `cart-list/${cartId}/`;
      const res = await apiInstance.get(url);
      const cartItems = res.data.cart_items || [];
      
      const validCartItems = cartItems.filter(item => item && item.product);
      setCart(validCartItems);
      setCartCount(validCartItems.length);
      
      const quantities = {};
      validCartItems.forEach((item) => { quantities[item.id] = item.qty; });
      setProductQuantities(quantities);
    } catch (error) {
      setCart([]);
      setCartCount(0);
    }
  };

  const fetchCartTotal = async (cartId, userId) => {
    try {
      const url = userId ? `cart-detail/${cartId}/${userId}/` : `cart-detail/${cartId}/`;
      const res = await apiInstance.get(url);
      setCartTotal(res.data);
    } catch (error) {
      console.error("Error fetching cart total:", error);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    if (!cart_id || userData === undefined) return;
    
    const userId = userData?.user_id || null;
    hasFetched.current = true;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        await fetchCartData(cart_id, userId);
        await fetchCartTotal(cart_id, userId);
      } catch (error) {
        setCart([]);
        setCartCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleQuantityChange = async (cartItemId, change) => {
    const cartItem = cart.find(item => item.id === cartItemId);
    if (!cartItem || !cartItem.product || !cart_id) return;

    const currentQty = productQuantities[cartItemId] || cartItem.qty;
    const requestedQty = currentQty + change;
    const validatedQty = validateQuantity(cartItem, requestedQty);

    if (validatedQty === currentQty) return;

    setProductQuantities(prev => ({ ...prev, [cartItemId]: validatedQty }));

    try {
      await apiInstance.patch(`cart-update/${cart_id}/${cartItemId}/`, { qty: validatedQty });
      await fetchCartData(cart_id, userData?.user_id);
      await fetchCartTotal(cart_id, userData?.user_id);
    } catch (error) {
      setProductQuantities(prev => ({ ...prev, [cartItemId]: currentQty }));
      toast.error(t('updateError') || 'Error al actualizar');
    }
  };

  const validateQuantity = (cartItem, newQty) => {
    if (!cartItem || !cartItem.product) return 1;
    const minQty = 1;
    const maxQty = Math.min(cartItem.product.max_cart_limit || 10, cartItem.product.stock_qty || 1);
    if (newQty < minQty) return minQty;
    if (newQty > maxQty) return maxQty;
    return newQty;
  };

  const handleDeleteCartItem = async (cartId, itemId) => {
    try {
      const url = userData?.user_id
        ? `cart-delete/${cartId}/${itemId}/${userData.user_id}/`
        : `cart-delete/${cartId}/${itemId}/`;
      await apiInstance.delete(url);
      
      const updatedCart = cart.filter(item => item.id !== itemId);
      setCart(updatedCart);
      setCartCount(updatedCart.length);
      fetchCartTotal(cart_id, userData?.user_id);
      toast.success(t('itemRemoved') || 'Producto eliminado');
    } catch (error) {
      toast.error(t('deleteError') || 'Error al eliminar');
    }
  };

  const createOrder = async () => {
    if (!isLoggedIn()) {
      toast.warning(t('loginRequired') || 'Inicia sesión para continuar');
      return;
    }

    if (!fullName || !mobile || !address || !city || !state || !country) {
      toast.warning(t('fillAllFields') || 'Completa todos los campos');
      return;
    }

    try {
      setLoading(true);
      const orderData = {
        full_name: fullName, email, phone: mobile, address, city, state, country,
        cart_id, user_id: userData ? userData.user_id : 0
      };

      const response = await apiInstance.post('create-order/', orderData);
      
      if (response.data?.order_oid) {
        setCart([]);
        setCartCount(0);
        toast.success(t('orderPlaced') || 'Pedido creado');
        navigate(`/checkout/${response.data.order_oid}`);
      }
    } catch (error) {
      toast.error(t('orderError') || 'Error al crear el pedido');
    } finally {
      setLoading(false);
    }
  };

  const sendWhatsAppMessage = async () => {
    if (loading) return; // Prevent double-clicks
    
    if (!fullName || !mobile || !address || !city || !state || !country) {
      toast.warning(t('fillAllFields') || 'Completa todos los campos');
      return;
    }
    if (!cart || cart.length === 0) {
      toast.warning(t('emptyCart') || 'Tu carrito está vacío');
      return;
    }

    try {
      setLoading(true);
      const orderData = {
        full_name: fullName, email, phone: mobile, address, city, state, country,
        cart_id, user_id: userData ? userData.user_id : 0, payment_method: 'whatsapp'
      };

      const response = await apiInstance.post('create-order/', orderData);
      
      if (!response.data?.order_oid) throw new Error('No order ID');

      const orderDate = new Date().toLocaleDateString('es-PY', { 
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
      });
      
      // Build a compact message to avoid URL length issues on mobile browsers
      let message = `*ASMAR SHOP*\n`;
      message += `Pedido #${response.data.order_oid}\n`;
      message += `Fecha: ${orderDate}\n\n`;
      message += `*Cliente:* ${fullName}\n`;
      message += `Tel: ${mobile}\n`;
      if (email) message += `Email: ${email}\n`;
      message += `\n*Envio:* ${address}, ${city}, ${state}, ${country}\n\n`;
      message += `*Productos:*\n`;
      
      cart.forEach((item, index) => {
        const colorText = item.color && item.color !== 'Sin Color' ? ` (${item.color})` : '';
        const sizeText = item.size && item.size !== 'Sin Talla' ? ` [${item.size}]` : '';
        message += `${index + 1}. ${item.product.title}${colorText}${sizeText} x${item.qty} - $${formatCurrency(item.qty * parseFloat(item.price))}\n`;
      });
      
      message += `\n*TOTAL: $${formatCurrency(cartTotal.total)}*\n`;
      message += `Gracias por tu pedido!`;
      
      const cleanNumber = whatsappNumber.replace(/[^0-9]/g, '');
      
      // Truncate message if too long (WhatsApp URL limit)
      const maxLength = 4000;
      const finalMessage = message.length > maxLength ? message.substring(0, maxLength) + '...' : message;
      const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(finalMessage)}`;
      
      // Clear cart before redirect to prevent stale state
      setCart([]);
      setCartCount(0);

      toast.success(`Pedido #${response.data.order_oid} creado`);
      
      // Small delay then redirect — prevents browser freezing from immediate redirect
      setTimeout(() => {
        window.location.href = whatsappUrl;
      }, 300);
      
    } catch (error) {
      console.error('Order error:', error);
      toast.error(t('orderError') || 'Error al crear el pedido');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: t('cart') || 'Carrito', icon: ShoppingCart },
    { num: 2, label: t('shipping') || 'Envío', icon: Truck },
    { num: 3, label: t('payment') || 'Pagar', icon: CreditCard }
  ];

  // ── EMPTY cart ──
  if (!loading && cart.length === 0 && hasFetched.current) {
    return (
      <div className="bg-background min-h-[80vh] flex items-center justify-center px-4 py-12">
        <div className="text-center max-w-sm w-full">
          <div className="w-20 h-20 rounded-full bg-secondary/60 flex items-center justify-center mx-auto mb-5">
            <ShoppingBag size={36} className="text-muted-foreground" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">{t('emptyCart') || 'Tu carrito está vacío'}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t('addItemsToCart') || 'Agrega productos para comenzar'}</p>
          <Link
            to="/products/"
            className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 active:scale-95"
          >
            {t('continueShopping') || 'Ver Productos'}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen pb-32 md:pb-16">
      {/* Compact Header */}
      <div className="bg-primary py-6 md:py-10 px-4 text-center">
        <h1 className="text-xl md:text-3xl font-bold text-primary-foreground">
          {t('cart') || 'Carrito'}
        </h1>
        {cart.length > 0 && (
          <p className="text-primary-foreground/60 text-xs md:text-base mt-1">
            {cart.length} {cart.length === 1 ? (t('item') || 'producto') : (t('items') || 'productos')}
          </p>
        )}
      </div>

      {/* Step Indicator — compact on mobile */}
      <div className="max-w-xl mx-auto -mt-4 mb-4 md:mb-8 px-3 md:px-5 relative z-10">
        <div className="bg-card rounded-xl md:rounded-2xl p-3 md:p-5 shadow-md border border-border">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.num}>
                <button
                  onClick={() => step.num < currentStep && setCurrentStep(step.num)}
                  className="flex flex-col items-center gap-1 md:gap-2"
                >
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center transition-all ${
                    currentStep >= step.num 
                      ? 'bg-accent text-accent-foreground shadow-sm' 
                      : 'bg-secondary text-muted-foreground'
                  }`}>
                    {currentStep > step.num ? (
                      <CheckCircle size={18} />
                    ) : (
                      <step.icon size={18} />
                    )}
                  </div>
                  <span className={`text-[10px] md:text-[13px] font-semibold ${
                    currentStep >= step.num ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {step.label}
                  </span>
                </button>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-[2px] md:h-[3px] rounded mx-2 md:mx-3 mb-5 md:mb-7 transition-all ${
                    currentStep > step.num ? 'bg-accent' : 'bg-secondary'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader size={36} className="text-accent animate-spin" />
        </div>
      )}

      {/* Main */}
      {!loading && cart.length > 0 && (
        <div className="max-w-6xl mx-auto px-3 md:px-5">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4 md:gap-8">
            {/* Left Column */}
            <div className="bg-card rounded-2xl md:rounded-3xl overflow-hidden shadow-sm border border-border">
              
              {/* STEP 1: Cart Items */}
              {currentStep === 1 && (
                <div className="p-3 md:p-6">
                  {/* Delivery Notice */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-3 mb-4">
                    <div className="flex items-start gap-2">
                      <Truck className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                        {t('brazilPickupNotice') || 'Entregas somente no Paraguay. Para Brasil e região, deve ser retirado na loja.'}
                      </p>
                    </div>
                  </div>
                  
                  <h3 className="text-base md:text-xl font-bold text-foreground mb-3 md:mb-6">
                    {t('cartItems') || 'Productos'} ({cart.length})
                  </h3>
                  
                  <div className="flex flex-col gap-3">
                    {cart.map((item) => {
                      const qty = productQuantities[item.id] || item.qty;
                      const unitPrice = parseFloat(item.price) || 0;
                      return (
                        <div
                          key={item.id}
                          className="flex gap-3 p-3 rounded-xl bg-secondary/20 border border-border/40"
                        >
                          {/* Image */}
                          <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden bg-card flex-shrink-0 border border-border/50">
                            <img
                              src={getVariantImage(item.product, item.color)}
                              alt={item.product.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          {/* Details */}
                          <div className="flex-1 min-w-0 flex flex-col">
                            <h4 className="text-sm md:text-base font-semibold text-foreground leading-tight line-clamp-2">
                              {item.product.title}
                            </h4>
                            
                            {/* Variants */}
                            <div className="flex gap-2 mt-1 flex-wrap">
                              {item.color && item.color !== 'Sin Color' && (
                                <span className="text-[11px] text-muted-foreground bg-secondary/60 px-2 py-0.5 rounded-full">
                                  {item.color}
                                </span>
                              )}
                              {item.size && item.size !== 'Sin Talla' && (
                                <span className="text-[11px] text-muted-foreground bg-secondary/60 px-2 py-0.5 rounded-full">
                                  {item.size}
                                </span>
                              )}
                            </div>
                            
                            {/* Bottom row: quantity, price, delete */}
                            <div className="flex items-center justify-between mt-auto pt-2 gap-2">
                              {/* Quantity control */}
                              <div className="flex items-center bg-card rounded-lg border border-border overflow-hidden">
                                <button
                                  onClick={() => handleQuantityChange(item.id, -1)}
                                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:bg-secondary active:bg-secondary/80 transition-colors"
                                >
                                  <Minus size={14} />
                                </button>
                                <span className="w-8 text-center text-sm font-bold text-foreground">
                                  {qty}
                                </span>
                                <button
                                  onClick={() => handleQuantityChange(item.id, 1)}
                                  className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:bg-secondary active:bg-secondary/80 transition-colors"
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                              
                              {/* Price */}
                              <span className="text-sm md:text-base font-bold text-foreground">
                                ${formatCurrency(unitPrice * qty)}
                              </span>
                              
                              {/* Delete */}
                              <button
                                onClick={() => handleDeleteCartItem(cart_id, item.id)}
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-destructive/60 hover:bg-destructive/10 hover:text-destructive active:scale-95 transition-all"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* STEP 2: Shipping */}
              {currentStep === 2 && (
                <div className="p-3 md:p-6">
                  <h3 className="text-base md:text-xl font-bold text-foreground mb-3 md:mb-6">
                    {t('shippingInfo') || 'Datos de Envío'}
                  </h3>
                  
                  <div className="flex flex-col gap-3">
                    <InputField icon={User} label={t('fullName') || 'Nombre completo'} value={fullName} onChange={setFullName} placeholder="Tu nombre completo" required />
                    <InputField icon={Phone} label={t('phone') || 'Teléfono'} type="tel" value={mobile} onChange={setMobile} placeholder="+595 981 123456" required />
                    <InputField icon={Mail} label={t('email') || 'Email'} type="email" value={email} onChange={setEmail} placeholder="tu@email.com" />
                    <InputField icon={MapPin} label={t('address') || 'Dirección'} value={address} onChange={setAddress} placeholder="Calle, número, barrio" required />
                    
                    <div className="grid grid-cols-2 gap-3">
                      <InputField icon={Building} label={t('city') || 'Ciudad'} value={city} onChange={setCity} placeholder="Ciudad" required />
                      <InputField icon={Map} label={t('state') || 'Departamento'} value={state} onChange={setState} placeholder="Depto." required />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">
                        {t('country') || 'País'} *
                      </label>
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="w-full py-3 px-3 rounded-xl border-2 border-border text-sm bg-card text-foreground outline-none transition-colors focus:border-accent"
                      >
                        <option value="Paraguay">Paraguay</option>
                        <option value="Brasil">Brasil</option>
                        <option value="Argentina">Argentina</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: Payment */}
              {currentStep === 3 && (
                <div className="p-3 md:p-6">
                  <h3 className="text-base md:text-xl font-bold text-foreground mb-4 md:mb-6">
                    {t('paymentMethod') || 'Método de Pago'}
                  </h3>
                  
                  {/* Order review */}
                  <div className="bg-secondary/20 rounded-xl p-3 mb-4 border border-border/40">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                      {t('orderReview') || 'Resumen del pedido'}
                    </p>
                    <div className="flex flex-col gap-1.5">
                      {cart.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span className="text-foreground/80 truncate mr-2">
                            {item.product.title} x{productQuantities[item.id] || item.qty}
                          </span>
                          <span className="font-semibold text-foreground flex-shrink-0">
                            ${formatCurrency((productQuantities[item.id] || item.qty) * parseFloat(item.price))}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Shipping to */}
                  <div className="bg-secondary/20 rounded-xl p-3 mb-5 border border-border/40">
                    <p className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">
                      {t('shippingTo') || 'Enviar a'}
                    </p>
                    <p className="text-sm text-foreground font-medium">{fullName}</p>
                    <p className="text-xs text-muted-foreground">{address}, {city}, {state}, {country}</p>
                    <p className="text-xs text-muted-foreground">{mobile}</p>
                  </div>
                  
                  <button
                    onClick={sendWhatsAppMessage}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl border-none text-white text-base font-bold transition-all active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed shadow-lg"
                    style={{ backgroundColor: '#25d366' }}
                  >
                    {loading ? (
                      <Loader size={22} className="animate-spin" />
                    ) : (
                      <MessageCircle size={22} />
                    )}
                    {loading ? (t('processing') || 'Procesando...') : (t('orderViaWhatsApp') || 'Pedir por WhatsApp')}
                  </button>
                  
                  <p className="text-center text-[11px] text-muted-foreground mt-3">
                    Se abrirá WhatsApp con tu pedido completo
                  </p>
                </div>
              )}
            </div>

            {/* Right Column - Order Summary (hidden on mobile at step 2/3, always visible on desktop) */}
            <div className={`${currentStep > 1 ? 'hidden lg:block' : ''}`}>
              <div className="bg-card rounded-2xl md:rounded-3xl p-4 md:p-6 shadow-sm border border-border lg:sticky lg:top-24">
                <h3 className="text-base md:text-xl font-bold text-foreground mb-4 md:mb-6">
                  {t('orderSummary') || 'Resumen'}
                </h3>
                
                {/* Items mini list */}
                <div className="flex flex-col gap-2 mb-4 max-h-[200px] overflow-y-auto">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                        <img src={getVariantImage(item.product, item.color)} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground truncate">{item.product.title}</p>
                        <p className="text-[10px] text-muted-foreground">x{productQuantities[item.id] || item.qty}</p>
                      </div>
                      <span className="text-xs font-bold text-foreground">
                        ${formatCurrency((productQuantities[item.id] || item.qty) * parseFloat(item.price))}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border/50 pt-3 mb-3">
                  <div className="flex justify-between text-sm text-foreground/70 mb-1.5">
                    <span>{t('subtotal') || 'Subtotal'}</span>
                    <span className="font-semibold text-foreground">${formatCurrency(cartTotal.sub_total)}</span>
                  </div>
                  {parseFloat(cartTotal.shipping) > 0 && (
                    <div className="flex justify-between text-sm text-foreground/70">
                      <span>{t('shipping') || 'Envío'}</span>
                      <span className="font-semibold text-foreground">${formatCurrency(cartTotal.shipping)}</span>
                    </div>
                  )}
                </div>
                
                <div className="border-t-2 border-border pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-base md:text-lg font-bold text-foreground">{t('total') || 'Total'}</span>
                    <span className="text-xl md:text-2xl font-bold text-accent">
                      ${formatCurrency(cartTotal.total)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Bottom Navigation Bar — mobile-first */}
      {!loading && cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-[0_-4px_20px_rgba(0,0,0,0.08)]" style={{ maxWidth: '100vw', overflow: 'hidden', willChange: 'transform' }}>
          <div className="max-w-6xl mx-auto px-3 md:px-5 py-3 flex items-center gap-2 sm:gap-3">
            {/* Total (always visible) */}
            <div className="flex flex-col min-w-0 flex-shrink-0">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">{t('total') || 'Total'}</span>
              <span className="text-base sm:text-lg md:text-xl font-bold text-accent leading-tight truncate">${formatCurrency(cartTotal.total)}</span>
            </div>

            <div className="flex-1 flex gap-2 justify-end min-w-0">
              {currentStep > 1 && (
                <button
                  onClick={prevStep}
                  className="flex items-center gap-1 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border-2 border-border text-muted-foreground text-sm font-semibold transition-all active:scale-95 flex-shrink-0"
                >
                  <ArrowLeft size={16} />
                  <span className="hidden sm:inline">{t('back') || 'Atrás'}</span>
                </button>
              )}
              
              {currentStep < 3 ? (
                <button
                  onClick={nextStep}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl bg-accent text-accent-foreground text-sm font-bold transition-all active:scale-95 shadow-md min-w-0 truncate"
                >
                  <span className="truncate">{currentStep === 1 ? (t('checkout') || 'Continuar') : (t('continue') || 'Siguiente')}</span>
                  <ArrowRight size={16} className="flex-shrink-0" />
                </button>
              ) : (
                <button
                  onClick={sendWhatsAppMessage}
                  disabled={loading}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl text-white text-sm font-bold transition-all active:scale-95 shadow-md disabled:opacity-70 min-w-0 truncate"
                  style={{ backgroundColor: '#25d366' }}
                >
                  <MessageCircle size={18} />
                  {loading ? '...' : (t('orderViaWhatsApp') || 'Pedir')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable input field component
function InputField({ icon: Icon, label, value, onChange, placeholder, type = 'text', required = false }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-foreground mb-1.5">
        {label} {required && '*'}
      </label>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full py-3 px-3 pl-10 rounded-xl border-2 border-border text-sm bg-card text-foreground placeholder:text-muted-foreground outline-none transition-colors focus:border-accent"
          required={required}
        />
      </div>
    </div>
  );
}

export default Cart;
